/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { topRatedDish } from '../../../auth/core/_requests';
import { useAuth } from '../../../auth/core/Auth';

interface Dish {
  dishImage: string;
  name: string;
  uuid: string;
  images: string;
  mrpPrice: string;
  type: {
    typeIcon: string;
  };
  dishreviews: {
    reviews?: number;
    averageReviews?: number;
  }[];
  dishvariants: {
    price: number;
    finalPrice: number;
  }[];
  avgRating?: number; // ⬅️ We add this to store computed average
}

const TopRatedDish = () => {
  const [data, setData] = useState<Dish[]>([]);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const chefId = currentUser?.uuid;

  const viewDetails = () => {
    navigate('/dish/list');
  };

  useEffect(() => {
    topRatedDish(chefId)
      .then((res: Dish[]) => {
        const withAvgRating = res.map((dish) => {
          const total = dish.dishreviews.reduce(
            (sum, r) => sum + Number(r.reviews ?? r.averageReviews ?? 0),
            0
          );
          const avgRating = dish.dishreviews.length > 0 ? total / dish.dishreviews.length : 0;
          return { ...dish, avgRating };
        });

        const sorted = withAvgRating.sort((a, b) => b.avgRating! - a.avgRating!);
        setData(sorted);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="snippet-block btm-same-height">
      <div className="head">
        <h6 className="mb-0 fw-bold">Most Rated Dishes</h6>
        <a href="#" className="text-link" onClick={viewDetails}>
          View All
          <i className="material-symbols-outlined fall">arrow_forward</i>
        </a>
      </div>
      <div className="snippet-content">
        {data?.length > 0 ? (
          data.map((item, i) => (
            <div key={i} className="dish-card mb-4">
              <div className="dish">
                <div className="thumbnail">
                  <img src={item.dishImage} alt={item.name} />
                </div>
                <div className="content">
                  <img className="category-ico" src={item.type.typeIcon} alt={item.name} />
                  <p className="mb-0 text-truncate">
                    <small>{item.name}</small>
                  </p>
                  {item.avgRating !== undefined && (
                    <div className="rating">
                      <img
                        className="ico fs-3"
                        style={{ width: "23px" }}
                        src="/assets/images/star.svg"
                        alt="Star"
                      />
                      {item.avgRating.toFixed(1)}
                    </div>
                  )}
                </div>
              </div>
              <p className="price mb-0 text-end pe-1 mid-gray">
                {item.dishvariants?.length > 0 ? (
                  <small>$ {item.dishvariants[0].finalPrice}</small>
                ) : (
                  <small>$ 0</small>
                )}
              </p>
            </div>
          ))
        ) : (
          <div className="text-center">
            <div className="thumbnail mt-5">
              <img src="/assets/images/noDishes.svg" style={{ height: "50px" }} />
            </div>
            No Rated Dishes
          </div>
        )}
      </div>
    </div>
  );
};

export { TopRatedDish };
